/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
 */

import workspaceService from "@/services/WorkspaceService.js";
import { groupsApi } from "@/AlfrescoApi.js";
import QuickLRU from "quick-lru";
import { getTargetRoute } from "@/services/NodeToMenuItemService.js";

/**
 * Service for generating menu structures dynamically
 */
class MenuService {
  constructor() {
    this.lruPristyAdmin = new QuickLRU({
      maxSize: 1,
      maxAge: 5 * 60 * 1000, // 5m
    });
  }
  clearAdminCache() {
    this.lruPristyAdmin.clear();
  }

  isInPristyAdminGroup() {
    const cacheKey = "isAdmin";
    if (this.lruPristyAdmin.has(cacheKey)) {
      return Promise.resolve(this.lruPristyAdmin.get(cacheKey));
    }
    return groupsApi
      .listGroupMembershipsForPerson("-me-")
      .then((data) => {
        const isAdmin = data.list.entries.some(
          (entry) => entry.entry.id === "GROUP_PRISTY_ADMINISTRATORS",
        );
        this.lruPristyAdmin.set(cacheKey, isAdmin);
        return isAdmin;
      })
      .catch((error) => {
        console.error("Error retrieving group memberships:", error);
        return false;
      });
  }

  async computeMenuActes() {
    let menu = [];
    let actesSpacesResult = await workspaceService.searchAllWorkspaces(
      "AND pm:typeEspace:'actes'",
    );
    // tous les espaces actes
    let actesSpaces = this.actesSiteToMenuItems(actesSpacesResult);
    menu.push({
      label: "Actes administratifs",
      key: "menuActes",
      to: { name: "mes-espaces-actes" },
      class: "layout-menuitem-root-text",
      items: actesSpaces,
    });
    return menu;
  }

  actesSiteToMenuItems(actesWorkspaces) {
    let actesItems = actesWorkspaces.map((acteWorkspace) => {
      return {
        label: acteWorkspace.title,
        class: "menu-item responsive-text",
        icon: "ri-folder-2-line",
        to: { name: "seances", params: { id: acteWorkspace.name } },
      };
    });
    actesItems.push({
      label: "Recherche",
      icon: "ri-search-line",
      class: "menu-item responsive-text",
      to: { name: "recherche-actes" },
    });
    return actesItems;
  }

  favoritesSiteToMenuItems(favorites) {
    return favorites
      .filter(
        (favorite) =>
          Object.prototype.hasOwnProperty.call(favorite["target"], "site") &&
          !favorite?.properties?.["pm:typeEspace"],
      )
      .map((favorite) => {
        return {
          label: favorite.target.site.title,
          icon: "ri-building-line",
          class: "menu-item responsive-text",
          to: {
            name: "navigation",
            params: { id: favorite.target.site.id },
          },
        };
      });
  }

  favoritesNodeToMenuItems(favorites) {
    return favorites.reduce((favoriteItems, favorite) => {
      if (!favorite.target) {
        console.warn(
          "favoritesNodeToMenuItems, favorite has no target",
          favorite,
        );
        return favoriteItems;
      }
      const target = favorite.target;
      const targetType = target.folder || target.file || target.site;

      if (!targetType || !targetType.path) {
        console.warn(
          "favoritesNodeToMenuItems, target has no valid path",
          favorite,
        );
        return favoriteItems;
      }

      const sitePath = targetType.path.elements.find(
        (element) => element.nodeType === "st:site",
      );

      if (sitePath?.name) {
        let favoriteItem = this.retrieveFavorite(favorite);
        favoriteItems.push(favoriteItem);
      }
      return favoriteItems;
    }, []);
  }

  /**
   * Transforms a SiteEntry in an object corresponding to VueJS Menu component items
   * @param favorite to add in menu
   * @returns a MenuItem
   */
  siteToMenuItem(favorite) {
    let isActe = favorite.properties?.["pm:typeEspace"] === "actes";
    return {
      label: favorite.title,
      icon: isActe ? "ri-folder-2-line" : "ri-building-line",
      class: "menu-item responsive-text",
      to: isActe
        ? {
            name: "seances",
            params: { id: favorite.name },
          }
        : {
            name: "navigation",
            params: { id: favorite.name },
          },
    };
  }

  retrieveFavorite(favorite) {
    const favoriteTarget = favorite.target;
    const favoriteTargetType =
      favoriteTarget.folder || favoriteTarget.file || favoriteTarget.site;
    if (!favoriteTargetType.path) {
      console.warn("nodeToMenuItem, favorite has no path", favorite);
      return;
    }

    let target = "";
    const sitePath = favoriteTargetType.path.elements.find(
      (element) => element.nodeType === "st:site",
    );
    let route;
    if (favoriteTargetType.isFolder) {
      route = {
        name: "navigation-dossier",
        params: { id: sitePath.name, FolderId: favorite.targetGuid },
      };
    } else {
      route = getTargetRoute(favoriteTargetType);
      if (route.name === "edit-collabora") {
        target = "_blank";
      }
    }
    if (sitePath?.name) {
      return {
        label: favoriteTargetType.name,
        icon: favoriteTargetType.isFolder ? "ri-folder-2-line" : "ri-file-line",
        class: "menu-item responsive-text",
        target: target,
        to: route,
      };
    }
  }
}

export default new MenuService();
